/**
 * 详情页的**买家叙事层**。
 *
 * ---------------------------------------------------------------------------
 * 为什么有这个文件（2026-09-29 用户报障）
 * ---------------------------------------------------------------------------
 * 用户原话：「详情页的生成提示词太傻了，要是淘宝这种一定是连贯的，
 * 而且详情页模块顺序不要写出来」。
 *
 * 上一版把「规划」这件事交给了用户：出图参数里有个可编辑的
 * 「详情页模块顺序」文本框，用户写几行词（主视觉 / 核心痛点 / 解决方案 …），
 * 每个词直接变成一张图的**唯一指令**。三个问题因此一起出现：
 *
 *   1. **词命不中官方模块名** —— 用户写「解决方案」而不是「详情 · 卖点拆解」，
 *      匹配不上就退化成同一句通用模板（「围绕『解决方案』解决一个具体的买家问题」），
 *      六屏的任务描述除模块名以外逐字相同；
 *   2. **没有跨屏信息** —— 每份提示词只讲自己那一屏，模型不知道第 3 屏该承接
 *      第 2 屏什么，于是标题互相重复（真跑出来过「解决上火烦恼」挨着「解决方案」）；
 *   3. **交付形态被一句规则带偏** —— 逐张提示词里带着 `ONE_SHOT_RULE`，
 *      它写着「一个产品完整占据画面」。那是**套图/主图**的规则（防 N 张拼贴），
 *      用在详情页上直接把「不要每屏都把完整产品当主体」这条硬规则顶掉了 ——
 *      六屏全成了「产品居中 + 左上大标题 + 底部小标签」的同一张图。
 *
 * 现在两端都不靠手写：
 *   · **屏数与顺序** = 平台规格里的模块（`platformDetails.ts`，官方规定，不可动）；
 *   · **每屏的语义角色** = 模块自带的 `beat` 字段（同文件，一份真源）；
 *   · **每屏该说什么话** = 本文件按角色派生（买家问题 / 视觉主体 / 构图尺度 /
 *     完整产品是否出现 / 最常犯的错）；
 *   · **屏与屏的关系** = 按**实际顺序**算前后承接（不是按固定模板套位置），
 *     所以换平台、换族、改屏数，叙事线自动跟着变。
 *
 * 这一层不产出任何尺寸 —— 尺寸只有 `platformDetails.ts` 一份。
 */
import { moduleSlots, type DetailBeatRole, type DetailModule, type DetailSlot, type DetailSpec } from './platformDetails'

/** 构图尺度 —— 详情页连续三屏都用同一个尺度是「傻」的主因之一 */
export type BeatScale = 'wide' | 'mid' | 'macro'

export const SCALE_NAME: Record<BeatScale, string> = {
  wide: '远景 —— 环境与全貌，主体只占画面一部分',
  mid: '中景 —— 主体与动作，产品可辨且能看到使用关系',
  macro: '微距 —— 局部与材质，画面里只有一小块表面或一个结构件',
}

/** 一屏在提示词里的完整说法 */
export type BeatRule = {
  /** 买家视角的问题 —— 这一屏存在的唯一理由 */
  question: string
  /** 画面主体该是什么 */
  subject: string
  /** 构图尺度 */
  scale: BeatScale
  /** 完整产品是否出现在这一屏 */
  fullProduct: boolean
  /** 这一屏最常犯的错（负向约束比正向描述更能拦住塌缩） */
  avoid: string
}

/**
 * 十个角色 → 该说什么话。**唯一真源**。
 *
 * `avoid` 是刻意写的负向约束。正向描述（「用微距强调质感」）模型经常照做一半
 * （给个材质特写但产品整体还在画面里），而负向约束（「这一屏不要出现完整产品」）
 * 拦得住 —— 尤其是 `craft` / `spec` 这两屏，它们**必须**不出现完整产品，
 * 否则整组又会塌回「每屏都是产品定妆」。
 */
const BEAT_RULES: Record<DetailBeatRole, BeatRule> = {
  identity: {
    question: '这是什么、长什么样、颜色和材质看着对不对',
    subject: '产品完整正视，纯白或极浅背景，边缘干净无杂物',
    scale: 'mid',
    fullProduct: true,
    avoid: '不要加场景、不要加卖点文案、不要加投影与道具 —— 这一屏只负责让人看清是什么',
  },
  hook: {
    question: '这个东西凭什么值得我往下看',
    subject: '产品定妆在真实生活氛围里，主体偏一侧，另一侧留出主标题的安全区',
    scale: 'wide',
    fullProduct: true,
    avoid: '不要把它做成另一张白底图；环境必须能暗示使用场景，否则往下看没有理由',
  },
  need: {
    question: '我是不是也有这个麻烦',
    subject: '痛点现场本身 —— 旧产品、错误用法、狼狈瞬间、要反复处理的麻烦；产品只作为对照出现在画面一角，不占主体',
    scale: 'mid',
    fullProduct: false,
    avoid: '不要把产品画成主角、不要把这一屏变成产品展示；情绪要具体到场景（真实的桌面、真实的手、真实的光），不要抽象氛围图',
  },
  proof: {
    question: '它凭什么能解决这个麻烦',
    subject: '机制 / 结构 / 材料 / 成分的**直接证据** —— 可以是剖解、截面、图解或标注位，一个画面只证一件事',
    scale: 'mid',
    fullProduct: true,
    avoid: '不要把三个卖点堆在同一屏却不给证据；不要只有结论没有依据（写「吸水强」不如画出水被吸走的那一秒）',
  },
  craft: {
    question: '做工扎实吗、会不会用两次就坏',
    subject: '材质纹理、缝线、表面处理或关键结构件的**微距特写**，光线干净，用质感本身说话',
    scale: 'macro',
    fullProduct: false,
    avoid: '**这一屏不要以完整产品为主体** —— 上一版每屏都是产品居中定妆，材质屏因此彻底废掉；也不要只是「放大版的产品图」',
  },
  life: {
    question: '在我自己的生活里用起来是什么样',
    subject: '真实场景中产品正在被使用，手部或人物动作入镜以体现尺度与操作感',
    scale: 'mid',
    fullProduct: true,
    avoid: '不要影棚摆拍感；光线要自然、环境要有生活痕迹（真实台面、真实房间），否则「真实使用」是假的',
  },
  spec: {
    question: '会不会买错、合不合我的尺寸与场景',
    subject: '产品正视 + 尺寸标线或比例参照物，画面一侧留出干净的参数区',
    scale: 'mid',
    fullProduct: true,
    avoid: '画面本身不要生成参数表格文字（表格后期加）；不要混进促销信息与价格',
  },
  compare: {
    question: '和我正在用的、或看过的别的方案比，好在哪',
    subject: '与替代方案或旧款的横向对比，构图对称、同一光源、同一机位',
    scale: 'wide',
    fullProduct: true,
    avoid: '不要出现别的品牌名称与 logo；不要贬低式对比（写对手差）—— 靠自己的证据站住',
  },
  package: {
    question: '到手是什么样、配件齐不齐、好不好收',
    subject: '包装形态、配件平铺或收纳状态，有秩序感',
    scale: 'wide',
    fullProduct: true,
    avoid: '不要做成礼物感（除非产品本身是礼品）；平铺不要杂乱堆叠，配件要看得出件数与用途',
  },
  trust: {
    question: '买错了怎么办、凭什么信这家',
    subject: '包装与服务信息的视觉化呈现 + 使用结果的收尾画面，稳重克制',
    scale: 'mid',
    fullProduct: true,
    avoid: '不要编造质保年限、认证与销量数据；不要叫卖、不要咨询式 CTA 文案',
  },
}

/**
 * 产品类型策略 —— 同一批屏，四类产品的强调点不同。
 *
 * 这里**不给判断器**，直接把判断规则写进提示词让模型自己判：
 * 类目字符串（套图模式才有）推不出「消耗品还是礼品」，而模型看着产品名与卖点
 * 一眼就能判。程序硬猜反而是新的错误来源。
 */
export const PRODUCT_KIND_GUIDE = [
  '标品 / 功能型（有明确参数与用途）：按「实用 → 结构 → 操作 → 效率 → 可确认参数」组织，证据优先给结构剖解与实测对比',
  '非标品 / 审美型（靠外观与风格决策）：按「设计 → 美感 → 材质 → 风格适配 → 搭配 → 氛围」组织，证据优先给质感与场景融入',
  '消耗品（会用完、会复购）：按「质地/口味 → 使用频率 → 制备过程 → 包装便利 → 成分」组织，证据优先给真实现场与到手形态',
  '礼品 / 情绪价值型：按「包装 → 仪式感 → 收礼场景 → 展示价值 → 氛围」组织，证据优先给开箱与在场感',
]

/* -------------------------------------------------------------------------- */
/* 逐屏解构                                                                    */
/* -------------------------------------------------------------------------- */

/** 一屏 = 一次生图调用 */
export type PlannedShot = {
  /** 从 1 开始的屏号 */
  no: number
  /** 界面展示名（官方模块名 + 多图模块的格号 + 移动端标记） */
  name: string
  /** 官方模块名 —— 只用于界面与「本屏官方位置」，不作为画面指令 */
  moduleName: string
  /** 这一屏在买家决策链里的角色 */
  beat: DetailBeatRole
  /** 买家视角的问题（来自 BEAT_RULES） */
  question: string
  /** 画面主体该是什么 */
  subject: string
  scale: BeatScale
  /** 完整产品是否出现在这一屏 */
  fullProduct: boolean
  /** 这一屏最常犯的错 */
  avoid: string
  /** 官方给这一屏的任务（模块自带 + 多图模块的格位角色） */
  task: string
  /** 生成尺寸 —— **来自平台规格，本文件不产生尺寸** */
  size: string
  /** 画面内不得出现任何文字（合规硬约束） */
  textFree?: boolean
  /** 移动端专属资产（官方要求独立上传，不是桌面图裁切） */
  isMobile?: boolean
  /**
   * 官方序列的第几轮 —— **只在 >1 时出现**。
   *
   * 张数超过官方图位数时（用户自己敲了 12，而 TikTok Shop 只有 8 个图位），
   * 多出来的那些按官方顺序从头循环。这一屏是第 2 轮、第 3 轮的第几屏。
   *
   * 为什么要有这个字段而不是让消费方去数名字：提示词层要据此加一条
   * 「同模块第二次出现必须换构图」的硬要求（否则模型会出两张一样的图），
   * 靠字符串里找「第 N 轮」是在解析自己的展示文案 —— 文案一改就静默失效。
   */
  round?: number
}

/**
 * 移动端资产的指令。
 *
 * 之所以要把这么长一段写死：官方对全宽类模块要求 desktop / mobile
 * **两份独立文件**，而两者比例差得远（2.44:1 vs 4:3）。不给这段话，
 * 模型会照着上一张的构图缩一下交差 —— 而缩完在手机上就是主体被裁掉一半。
 */
const MOBILE_TASK =
  '这是同一屏的**移动端版本**。官方要求与桌面图分别上传，禁止拿桌面图裁切或拉伸得到：' +
  '必须为竖屏重新构图 —— 主体放大、只保留一条短标题的位置、最多三个标签，' +
  '关键内容全部落在画面中心 86% 以内，边缘不放任何要素（手机上会被裁掉）。'

function shotOf(
  m: DetailModule,
  s: DetailSlot,
  si: number,
  slotCountOfModule: number,
  isMobile: boolean,
): Omit<PlannedShot, 'no'> {
  const rule = BEAT_RULES[m.beat]
  // 多图模块（A+ 三图 / 单图+侧栏）逐格不同：格位角色接在模块任务后面
  const cell = slotCountOfModule > 1 ? `本格画：${s.role}` : ''
  const base = cell ? `${m.task}。${cell}` : m.task
  return {
    name: `${m.name}${slotCountOfModule > 1 ? ` · 图 ${si + 1}/${slotCountOfModule}` : ''}${isMobile ? ' · 移动端' : ''}`,
    moduleName: m.name,
    beat: m.beat,
    question: rule.question,
    subject: isMobile ? `${rule.subject}（竖屏重构图，主体更大、要素更少）` : rule.subject,
    scale: rule.scale,
    fullProduct: rule.fullProduct,
    avoid: rule.avoid,
    task: isMobile ? `${base}。${MOBILE_TASK}` : base,
    size: isMobile ? (s.mobileSize ?? s.size) : s.size,
    textFree: m.textFree,
    isMobile: isMobile || undefined,
  }
}

/**
 * 本批逐屏解构 —— **页面与提示词共用这一个入口**。
 *
 * 上一版这份解构写在页面里（`longShots` / `shots` 两处 useMemo），
 * 提示词又是另一套拼装，两边各算一遍「这一屏是什么」。
 * 现在：页面拿它渲染卡片与逐张尺寸，提示词拿它渲染叙事线 —— 同一份。
 *
 * 顺序 = 官方模块顺序（规格层规定）；`count` 之后**循环**（见下）。
 * 多图模块展开成多屏、移动端资产插在对应屏之后 —— 与前端的报价口径一致。
 *
 * ## 张数可以超过官方图位数（2026-09-30 用户口径）
 *
 * 用户原话：「可以我们自己输入数量」。张数控件因此从一串固定档位变成**自由输入**，
 * 于是必然出现 `count > 官方图位数`（TikTok Shop 详情页模块只有 8 个图位，用户要 12 张）。
 *
 * 上一版这里是 `out.slice(0, count)` —— 要 12 张只给 8 张，**静默少出**，
 * 而报价按用户输的 12 张算。改成循环之后与 `mainShotsFor` 是同一套语义
 * （那边是「换机位」、这里是「官方序列第 M 轮」），但**不能省掉那个后缀**：
 * 不加的话逐张清单里会出现两行一模一样的「主图 · 白底」，看起来像 bug，
 * 提示词那边也会让模型画两张完全相同的图。
 */
export function planDetailShots(
  spec: DetailSpec,
  count: number,
  opts?: {
    withMobile?: boolean
    /**
     * **只走勾选的模块**（模块名；空/不给 = 全部模块）。
     *
     * 2026-10-07 用户口径：参考竞品「包含模块 0/16」的逐个勾选，
     * 选了「加 16 个模块逐个勾选」。⚠️ 我们的模块数跟着平台规格走，
     * 不是固定 16 —— 所以这里收的是**名字**，由页面从 `spec.modules` 渲染。
     *
     * ⚠️ 过滤后**必须仍按官方顺序**，不许按用户勾选的先后排：
     * 详情页的叙事线（`describePlan` 的承接关系）是按实际顺序前后算的，
     * 乱序会让「第 3 屏承接第 2 屏」指向一个不相干的模块。
     * 用 `spec.modules.filter(...)` 而不是遍历 `opts.modules` 就是这个意思。
     *
     * 交集为空（勾了别的平台的模块 / 草稿里全是失效名字）**回落到全部模块**：
     * 空清单会让这一档一张图都不出，而界面上一切正常 —— 那是静默的零产出。
     */
    modules?: string[]
    /**
     * **每个模块出几份**（模块名 → 份数；0 = 这个模块不出）。
     *
     * 2026-10-08 用户口径：「详情跟主图一样可以选择」—— 主图那一档按类型配张数，
     * 这一档原来只有勾选框。给了 `counts` 之后它**说了算**：
     *   · `modules` 被忽略（份数 0 就是不出，比名字清单多一维，不必两个都传）；
     *   · `count` 也被忽略（总张数 = 各模块份数 × 每份张数之和，这是「配比之和
     *     就是张数」那条规矩在详情档的形态 —— 两个入口写同一个量迟早对不上）。
     *
     * ⚠️ 「份数」不是「张数」：官方多图模块一份顶 3 张、`withMobile` 还要再加。
     * 换算在 `platformDetails.moduleShotCount`，这里只管**重复几轮**。
     *
     * ⚠️ 不传 `counts` 时走**老那一支**（`modules` + `count` 循环）：
     * 批量工作台（`pages/BatchStudio.tsx`）还按老口径调它，一次只改一条路径。
     */
    counts?: Record<string, number>
  },
): PlannedShot[] {
  const counts = opts?.counts
  const picked = opts?.modules?.length
    ? spec.modules.filter((m) => opts.modules!.includes(m.name))
    : []
  // 有 `counts` 时份数说了算：0 份 = 不出（与勾选框同义，只是多了一维「几轮」）。
  const use = counts
    ? spec.modules.filter((m) => (counts[m.name] ?? 0) > 0)
    : picked.length
      ? picked
      : spec.modules
  const base: { shot: Omit<PlannedShot, 'no'>; round: number }[] = []
  for (const m of use) {
    const slots = moduleSlots(m)
    // 没有 `counts` 时每个模块一律 1 份 —— 就是加「份数」这一维之前的行为
    const times = counts ? Math.max(0, Math.floor(counts[m.name] ?? 0)) : 1
    for (let t = 1; t <= times; t++) {
      slots.forEach((s, si) => {
        base.push({ shot: shotOf(m, s, si, slots.length, false), round: t })
        if (opts?.withMobile && s.mobileSize) {
          base.push({ shot: shotOf(m, s, si, slots.length, true), round: t })
        }
      })
    }
  }
  if (!base.length) return []
  // 有 `counts`：总张数就是序列长度（不再整体循环）；
  // 没有：仍按调用方给的 `count` 循环，超出的部分标「第 N 轮」。
  const n = counts ? base.length : Math.max(1, Math.floor(count) || 1)
  return Array.from({ length: n }, (_, i) => {
    const b = base[i % base.length]
    const round = counts ? b.round : Math.floor(i / base.length) + 1
    if (round <= 1) return { ...b.shot, no: i + 1 }
    return {
      ...b.shot,
      no: i + 1,
      round,
      name: `${b.shot.name} · 第 ${round} 轮`,
      task:
        `${b.shot.task}（「${b.shot.moduleName}」的第 ${round} 轮：` +
        `必须换构图、换角度或换场景，**不许出两张一样的**）`,
    }
  })
}

/* -------------------------------------------------------------------------- */
/* 叙事派生                                                                    */
/* -------------------------------------------------------------------------- */

export type DetailPlan = {
  /** 购买决策类型 —— 买家主要靠什么下这个单 */
  decisionType: string
  /** 页面策略 —— 整篇要走的路线 */
  strategy: string
  /** 最佳首屏方向 */
  firstScreen: string
  /** 视觉节奏 —— 尺度分布与完整产品的出现规律 */
  rhythm: string
  /** 买家需求排序（逐屏的问题，按实际顺序） */
  ladder: string[]
}

/**
 * 从**实际序列**派生策略与节奏，而不是按固定模板套位置。
 *
 * 为什么必须按实际顺序算：各族的模块顺序本来就不同（国内长图是
 * 白底 → 主视觉 → 痛点 → 卖点 → 材质 → 场景 → 规格 → 售后；
 * 抖音/Ozon 是白底 → 场景 → 卖点 → 细节 → 尺寸 → 配件 → 对比 → 售后）。
 * 按固定位置套语义，就会给「场景」那一屏讲「材质屏」的话。
 */
export function describePlan(shots: PlannedShot[]): DetailPlan {
  const roles = shots.map((s) => s.beat)
  const has = (r: DetailBeatRole) => roles.includes(r)
  const first = shots[0]

  /**
   * 购买决策类型 —— **只描述这一批屏覆盖了哪些决策环节**，不替产品下结论。
   *
   * ⚠️ 别按「缺哪类屏」反推产品类型：用户可以把 8 屏截成 6 屏，
   * 截断后没有规格屏，不代表「买家不看参数」。上一版按 `!has('spec')`
   * 判成「审美驱动型 —— 买家凭质感与风格下单，参数只是确认项」，
   * 对一罐蜂蜜说出这句话是错的（蜂蜜是消耗品，参数不是它的决策点，
   * 但结论对不上原因是两个）。
   * 买家到底靠什么决策，交给模型按产品判断 ——【产品类型策略】那一段
   * 已经写进每一屏的提示词里，不需要在这里替它先判一遍。
   */
  const decisionType = has('compare')
    ? '比较筛选型 —— 本批带横向对比环节，证据必须能横向对照，泛泛的好听话无效'
    : has('proof') && has('spec')
      ? '功能确认型 —— 本批含机制证据 + 参数确认，买家要确认「有没有用、会不会买错」'
      : has('craft') && has('life')
        ? '观感体验型 —— 本批靠质感细节与真实使用场景建立信任'
        : '场景说服型 —— 本批靠场景痛点与使用结果打动人'

  const strategy = [
    first?.beat === 'identity'
      ? '先让买家看清是什么（首屏白底/定妆），再从第二屏起逐屏给往下看的理由'
      : '首屏直接给使用氛围与主标题，让买家先产生代入，再补产品身份',
    has('need') ? '痛点必须画在具体场景里（真实的手、真实的台面），不要抽象氛围图' : '',
    has('proof') ? '能力证明屏只讲一个卖点并给出直接证据，不要在每屏重复罗列全部卖点' : '',
    has('craft') ? '做工屏用微距与质感说话，不以完整产品为主体' : '',
    has('spec') ? '规格屏只解决「会不会买错」，不承担促销' : '',
  ]
    .filter(Boolean)
    .join('；')

  const wide = shots.filter((s) => s.scale === 'wide').length
  const macro = shots.filter((s) => s.scale === 'macro').length
  const withProduct = shots.filter((s) => s.fullProduct).length
  const rhythm =
    `全篇 ${shots.length} 屏：远景 ${wide} 屏 / 微距 ${macro} 屏 / 其余中景 —— ` +
    `相邻两屏不得使用同一构图；只有 ${withProduct} 屏以完整产品为主体，` +
    `其余 ${shots.length - withProduct} 屏把主体让给场景、局部或信息`

  return {
    decisionType,
    strategy,
    firstScreen: first ? `${first.question}（${first.subject}）` : '',
    rhythm,
    ladder: shots.map((s) => s.question),
  }
}

/** 角色 → 中文短名，用于界面展示（「这一屏在干嘛」） */
export const BEAT_NAME: Record<DetailBeatRole, string> = {
  identity: '看清是什么',
  hook: '首屏印象',
  need: '需求现场',
  proof: '能力证明',
  craft: '做工细节',
  life: '真实使用',
  spec: '规格适配',
  compare: '横向对比',
  package: '到手形态',
  trust: '信任收尾',
}
